import { useState, useCallback, useRef, type ReactNode } from 'react';
import {
  TrashIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  DotsSixVerticalIcon,
  ArrowCounterClockwiseIcon,
} from '@phosphor-icons/react';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { columnStyleOf, type ColumnSorting } from '@erp/shared-types';
import type {
  ColumnViewSource,
  CustomColumnConfig,
} from '@/hooks/useColumnConfig';
import { SortPanel } from '@/components/procurement/query-builder/SortPanel';
import {
  MAX_SORT_LEVELS,
  addSortLevel,
  changeSortLevel,
  moveSortLevel,
  removeSortLevel,
  sortDrafts,
  sortOptions,
} from './columnConfigSorting';
import { newColumnFromField, type FieldOption } from './columnConfigFields';
import { columnSourceKey } from './schema-extensions/extensionColumns';
import { cn } from '@/lib/utils';
import { Num, T, useGT } from 'gt-react';

export type { FieldOption } from './columnConfigFields';

export interface ColumnConfigDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  columns: CustomColumnConfig[];
  /** Omit for a fixed column set: the Add Column section is not shown. */
  onAdd?: (config: Omit<CustomColumnConfig, 'id'>) => void;
  /** Omit when Hide is the only way to drop a column. */
  onRemove?: (id: string) => void;
  onUpdate?: (
    id: string,
    updates: Partial<Omit<CustomColumnConfig, 'id'>>,
  ) => void;
  /** Whether to offer filtering controls (defaults to true). */
  showFilterToggle?: boolean;
  /** Whether to offer the per-column Sort switch (defaults to true). */
  showSortToggle?: boolean;
  /** Whether to offer the per-column Bold switch (defaults to false). */
  showBoldToggle?: boolean;
  /** Callback to reorder columns via drag-and-drop */
  onReorder?: (fromIndex: number, toIndex: number) => void;
  /** Callback to reset columns to company defaults */
  onReset?: () => void;
  /** Callback to save current config as the org-wide default (admin only) */
  onSaveAsOrgDefault?: () => void;
  /** Available fields that can be added as columns */
  fields: FieldOption[];
  fieldStatus?: ReactNode;
  /** The ordered multi-column sort saved with the view. */
  sorting?: ColumnSorting;
  /** Shows the Sort Order section when given. */
  onSortingChange?: (sorting: ColumnSorting) => void;
  /** Levels the sort can hold here; Add sort is off once it is full. */
  maxSortLevels?: number;
  /** Whose layout is showing, named under the title. */
  viewSource?: ColumnViewSource;
  /** A short note beside a column without an API field, by column id. */
  columnNotes?: Readonly<Record<string, string>>;
}

type ColumnUpdate = Partial<Omit<CustomColumnConfig, 'id'>>;

/** One per-column checkbox: its header label, its row label, and what it saves. */
interface ColumnToggleOption {
  key: string;
  header: ReactNode;
  label: ReactNode;
  checked: (col: CustomColumnConfig) => boolean;
  update: (col: CustomColumnConfig, checked: boolean) => ColumnUpdate;
  /** Extension columns sort and filter on the server only. */
  fixedForExtension?: boolean;
}

const SORT_TOGGLE: ColumnToggleOption = {
  key: 'sort',
  header: <T context="column config header label for sort column">Sort</T>,
  label: <T context="column config sortable checkbox label">Sort</T>,
  checked: (col) => col.sortable ?? false,
  update: (_col, checked) => ({ sortable: checked }),
  fixedForExtension: true,
};

const FILTER_TOGGLE: ColumnToggleOption = {
  key: 'filter',
  header: <T context="column config header label for filter column">Filter</T>,
  label: <T context="column config filterable checkbox label">Filter</T>,
  checked: (col) => col.filterable ?? false,
  update: (_col, checked) => ({ filterable: checked }),
  fixedForExtension: true,
};

const BOLD_TOGGLE: ColumnToggleOption = {
  key: 'bold',
  header: <T context="column config header label for bold text column">Bold</T>,
  label: <T context="column config bold text checkbox label">Bold</T>,
  checked: (col) => columnStyleOf(col).bold,
  update: (col, checked) => ({ style: { ...col.style, bold: checked } }),
};

const RIGHT_TOGGLE: ColumnToggleOption = {
  key: 'right',
  header: (
    <T context="column config header label for right-align column">Right</T>
  ),
  label: <T context="column config align right checkbox label">Right</T>,
  checked: (col) => col.alignRight ?? false,
  update: (_col, checked) => ({ alignRight: checked }),
};

const HIDE_TOGGLE: ColumnToggleOption = {
  key: 'hide',
  header: <T context="column config header label for hide column">Hide</T>,
  label: <T context="column config hidden checkbox label">Hide</T>,
  checked: (col) => col.hidden ?? false,
  update: (_col, checked) => ({ hidden: checked }),
};

/** The per-column checkboxes a table offers, in display order. */
function columnToggleOptions(show: {
  sort: boolean;
  filter: boolean;
  bold: boolean;
}): ColumnToggleOption[] {
  return [
    ...(show.sort ? [SORT_TOGGLE] : []),
    ...(show.filter ? [FILTER_TOGGLE] : []),
    ...(show.bold ? [BOLD_TOGGLE] : []),
    RIGHT_TOGGLE,
    HIDE_TOGGLE,
  ];
}

function ColumnToggles({
  col,
  onUpdate,
  toggles,
}: {
  col: CustomColumnConfig;
  onUpdate: NonNullable<ColumnConfigDialogProps['onUpdate']>;
  toggles: readonly ColumnToggleOption[];
}) {
  const isExtension = col.source?.kind === 'extension';
  return (
    <div className="flex items-center gap-3 shrink-0">
      {toggles.map((toggle) => (
        <label
          key={toggle.key}
          className="flex items-center gap-1 cursor-pointer"
        >
          <Checkbox
            checked={toggle.checked(col)}
            disabled={isExtension && toggle.fixedForExtension}
            onCheckedChange={(checked) =>
              onUpdate(col.id, toggle.update(col, checked === true))
            }
            className="h-3.5 w-3.5"
          />
          <span className="text-[10px] text-muted-foreground">
            {toggle.label}
          </span>
        </label>
      ))}
    </div>
  );
}

/** The labels over the per-column toggles, lined up with their checkboxes. */
function ColumnToggleHeaders({
  toggles,
  hasRemove,
}: {
  toggles: readonly ColumnToggleOption[];
  hasRemove: boolean;
}) {
  return (
    <div
      className={cn(
        'flex items-center gap-4 text-[10px] text-muted-foreground',
        hasRemove ? 'pr-9' : 'pr-1',
      )}
    >
      {toggles.map((toggle) => (
        <span key={toggle.key}>{toggle.header}</span>
      ))}
    </div>
  );
}

function SortableColumnRow({
  col,
  subtitle,
  toggles,
  onRemove,
  onUpdate,
}: {
  col: CustomColumnConfig;
  subtitle?: string;
  toggles: readonly ColumnToggleOption[];
  onRemove?: (id: string) => void;
  onUpdate?: (
    id: string,
    updates: Partial<Omit<CustomColumnConfig, 'id'>>,
  ) => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: col.id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const gt = useGT();
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(col.header);
  const inputRef = useRef<HTMLInputElement>(null);
  const closedByKeyboard = useRef(false);

  const commitEdit = () => {
    const trimmed = editValue.trim();
    if (trimmed && trimmed !== col.header && onUpdate) {
      onUpdate(col.id, { header: trimmed });
    } else {
      setEditValue(col.header);
    }
    setIsEditing(false);
  };

  const startEdit = () => {
    if (onUpdate) {
      setEditValue(col.header);
      setIsEditing(true);
    }
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-2 rounded-md border bg-background px-3 py-1.5 ${isDragging ? 'opacity-50 shadow-lg z-10' : ''}`}
    >
      <button
        type="button"
        className="cursor-grab active:cursor-grabbing touch-none shrink-0"
        {...attributes}
        {...listeners}
      >
        <DotsSixVerticalIcon size={16} className="text-muted-foreground" />
      </button>
      <div className="flex-1 min-w-0">
        {isEditing ? (
          <Input
            ref={inputRef}
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={() => {
              if (closedByKeyboard.current) {
                closedByKeyboard.current = false;
                return;
              }
              commitEdit();
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                closedByKeyboard.current = true;
                commitEdit();
              }
              if (e.key === 'Escape') {
                e.preventDefault();
                closedByKeyboard.current = true;
                setEditValue(col.header);
                setIsEditing(false);
              }
            }}
            className="h-6 text-sm font-medium px-1 py-0"
            autoFocus
          />
        ) : (
          <span
            className="text-sm font-medium cursor-text"
            tabIndex={0}
            role="button"
            aria-label={gt('Rename column {name}', {
              context:
                'aria-label for renaming a column in column config dialog',
              name: col.header,
            })}
            onDoubleClick={startEdit}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                startEdit();
              }
            }}
            title={gt('Double-click to rename', {
              context: 'tooltip for column header in column config dialog',
            })}
          >
            {col.header}
          </span>
        )}
        {subtitle && (
          <span className="text-xs text-muted-foreground ml-2">{subtitle}</span>
        )}
      </div>
      {onUpdate && (
        <ColumnToggles col={col} onUpdate={onUpdate} toggles={toggles} />
      )}
      {onRemove && (
        <Button
          size="sm"
          variant="ghost"
          className="shrink-0 h-7 w-7 p-0"
          onClick={() => onRemove(col.id)}
        >
          <TrashIcon className="h-3.5 w-3.5 text-muted-foreground" />
        </Button>
      )}
    </div>
  );
}

function ViewSourceNote({ source }: { source: ColumnViewSource }) {
  return (
    <span className="block text-xs text-muted-foreground">
      {source === 'user' ? (
        <T context="column config dialog: the table shows the user's own saved view">
          Showing your view. Reset to go back to the org default.
        </T>
      ) : source === 'org' ? (
        <T context="column config dialog: the table shows the admin-saved org default view">
          Showing the org default. Changes here save as your own view.
        </T>
      ) : (
        <T context="column config dialog: the table shows the built-in default view">
          Showing the standard layout. Changes here save as your own view.
        </T>
      )}
    </span>
  );
}

function SortOrderSection({
  columns,
  sorting,
  onSortingChange,
  maxLevels,
}: {
  columns: CustomColumnConfig[];
  sorting: ColumnSorting;
  onSortingChange: (sorting: ColumnSorting) => void;
  maxLevels: number;
}) {
  const options = sortOptions(columns);
  const full = sorting.length >= maxLevels;
  return (
    <div className="shrink-0 space-y-1.5 border-t pt-3">
      <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
        <T context="section header for the ordered multi-column sort in column config dialog">
          Sort Order
        </T>
      </div>
      <p className="text-xs text-muted-foreground">
        <T context="column config dialog: how the ordered sort works">
          Rows sort by the first level, then each next level breaks ties.
          Shift-click a column header to add a level.
        </T>
      </p>
      <SortPanel
        options={options}
        sorts={sortDrafts(sorting)}
        canAdd={!full}
        onAdd={() => onSortingChange(addSortLevel(sorting, options, maxLevels))}
        onRemove={(id) => onSortingChange(removeSortLevel(sorting, id))}
        onChange={(id, patch) =>
          onSortingChange(changeSortLevel(sorting, id, patch))
        }
        onReorder={(from, to) =>
          onSortingChange(moveSortLevel(sorting, from, to))
        }
      />
      {full && (
        <p className="text-xs text-muted-foreground">
          <T context="column config dialog: why Add sort is off once the sort holds its maximum number of levels">
            The sort holds <Num>{MAX_SORT_LEVELS}</Num> levels at most, counting
            levels on hidden columns. Remove a level to add another.
          </T>
        </p>
      )}
    </div>
  );
}

function AddColumnSection({
  fields,
  onAdd,
}: {
  fields: FieldOption[];
  onAdd: NonNullable<ColumnConfigDialogProps['onAdd']>;
}) {
  const gt = useGT();
  const [newHeader, setNewHeader] = useState('');
  const [selectedField, setSelectedField] = useState('');
  const [fieldPopoverOpen, setFieldPopoverOpen] = useState(false);

  const selectedEntry = fields.find(
    (f) => columnSourceKey(f) === selectedField,
  );

  const handleAdd = () => {
    const column = newColumnFromField(fields, selectedField, newHeader);
    if (!column) return;
    onAdd(column);
    setNewHeader('');
    setSelectedField('');
  };

  return (
    <div className="space-y-1.5">
      <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
        <T context="section header for add column in column config dialog">
          Add Column
        </T>
      </div>
      <div className="flex items-end gap-2">
        <div className="flex-1 space-y-1">
          <label className="text-xs text-muted-foreground">
            <T context="label for column title input in column config dialog">
              Title
            </T>
          </label>
          <Input
            placeholder={gt('e.g. Vendor #', {
              context:
                'placeholder for column title input in column config dialog',
            })}
            value={newHeader}
            onChange={(e) => setNewHeader(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleAdd();
            }}
            className="h-8"
          />
        </div>
        <div className="flex-1 space-y-1">
          <label className="text-xs text-muted-foreground">
            <T context="label for data field picker in column config dialog">
              Data Field
            </T>
          </label>
          <Popover open={fieldPopoverOpen} onOpenChange={setFieldPopoverOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className="w-full justify-start font-normal h-8"
              >
                <MagnifyingGlassIcon className="mr-2 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                {selectedEntry ? (
                  <span className="truncate text-sm">
                    {selectedEntry.label}
                  </span>
                ) : (
                  <span className="text-muted-foreground text-sm">
                    <T context="placeholder for data field picker in column config dialog">
                      Select field...
                    </T>
                  </span>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[280px] p-0" align="start">
              <Command>
                <CommandInput
                  placeholder={gt('Search fields...', {
                    context:
                      'search placeholder for field picker in column config dialog',
                  })}
                />
                <CommandList>
                  <CommandEmpty>
                    <T context="empty state for field picker in column config dialog">
                      No fields found.
                    </T>
                  </CommandEmpty>
                  <CommandGroup>
                    {fields.map((field) => (
                      <CommandItem
                        key={columnSourceKey(field)}
                        value={`${field.label} ${field.description ?? field.apiField} ${columnSourceKey(field)}`}
                        onSelect={() => {
                          setSelectedField(columnSourceKey(field));
                          if (!newHeader.trim()) {
                            setNewHeader(field.label);
                          }
                          setFieldPopoverOpen(false);
                        }}
                      >
                        <div className="flex flex-col">
                          <span>{field.label}</span>
                          <span className="text-xs text-muted-foreground">
                            {field.description ?? field.apiField} (
                            {field.format})
                          </span>
                        </div>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>
        <Button
          size="sm"
          className="h-8"
          onClick={handleAdd}
          disabled={!selectedEntry || !newHeader.trim()}
          aria-label={gt('Add Column', {
            context: 'add selected field in column configuration',
          })}
        >
          <PlusIcon className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

export function ColumnConfigDialog({
  open,
  onOpenChange,
  columns,
  onAdd,
  onRemove,
  onUpdate,
  onReorder,
  onReset,
  onSaveAsOrgDefault,
  showFilterToggle = true,
  showSortToggle = true,
  showBoldToggle = false,
  fields,
  fieldStatus,
  sorting,
  onSortingChange,
  maxSortLevels = MAX_SORT_LEVELS,
  viewSource,
  columnNotes,
}: ColumnConfigDialogProps) {
  const toggles = columnToggleOptions({
    sort: showSortToggle,
    filter: showFilterToggle,
    bold: showBoldToggle,
  });
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id || !onReorder) return;
      const oldIndex = columns.findIndex((c) => c.id === active.id);
      const newIndex = columns.findIndex((c) => c.id === over.id);
      if (oldIndex !== -1 && newIndex !== -1) {
        onReorder(oldIndex, newIndex);
      }
    },
    [columns, onReorder],
  );

  /** Build subtitle for each column row — always show apiField */
  const getSubtitle = (col: CustomColumnConfig): string | undefined => {
    if (col.source?.kind === 'extension') {
      const field = fields.find(
        (option) => columnSourceKey(option) === columnSourceKey(col),
      );
      return field?.description ?? col.source.field;
    }
    if (!col.apiField) return columnNotes?.[col.id];
    return col.apiField;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>
            <T context="dialog title for column config dialog">
              Configure Columns
            </T>
          </DialogTitle>
          <DialogDescription>
            {onAdd ? (
              <T context="dialog description for column config dialog">
                Drag to reorder, toggle sorting/filtering, or add new columns.
              </T>
            ) : (
              <T context="dialog description for column config dialog on a table with a fixed set of columns">
                Drag to reorder. Double-click a name to rename it.
              </T>
            )}
          </DialogDescription>
          {viewSource && <ViewSourceNote source={viewSource} />}
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-y-auto flex flex-col gap-4 py-2">
          {onAdd && <AddColumnSection fields={fields} onAdd={onAdd} />}
          {fieldStatus && (
            <div role="status" className="text-xs text-muted-foreground">
              {fieldStatus}
            </div>
          )}

          {/* Column list — scrollable */}
          <div className="shrink-0 border-t pt-3">
            <div className="flex items-center justify-between mb-1.5">
              <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                <T context="section header for column order in column config dialog">
                  Column Order
                </T>
              </div>
              {onUpdate && (
                <ColumnToggleHeaders
                  toggles={toggles}
                  hasRemove={onRemove !== undefined}
                />
              )}
            </div>
            <div
              className={cn(
                'overflow-y-auto space-y-1.5 pr-1',
                onSortingChange ? 'max-h-[32vh]' : 'max-h-[40vh]',
              )}
            >
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={columns.map((c) => c.id)}
                  strategy={verticalListSortingStrategy}
                >
                  {columns.map((col) => (
                    <SortableColumnRow
                      key={col.id}
                      col={col}
                      subtitle={getSubtitle(col)}
                      toggles={toggles}
                      onRemove={onRemove}
                      onUpdate={onUpdate}
                    />
                  ))}
                </SortableContext>
              </DndContext>
              {columns.length === 0 && (
                <div className="text-sm text-muted-foreground text-center py-4">
                  <T context="empty state for column list in column config dialog">
                    No columns configured. Add one above or reset to defaults.
                  </T>
                </div>
              )}
            </div>
          </div>
          {onSortingChange && (
            <SortOrderSection
              columns={columns}
              sorting={sorting ?? []}
              onSortingChange={onSortingChange}
              maxLevels={maxSortLevels}
            />
          )}
        </div>

        <DialogFooter className="flex-row justify-between sm:justify-between">
          <div className="flex items-center gap-2">
            {onReset && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onReset}
                className="text-muted-foreground"
              >
                <ArrowCounterClockwiseIcon className="h-4 w-4 mr-1.5" />
                <T context="button to reset columns to defaults in column config dialog">
                  Reset to Defaults
                </T>
              </Button>
            )}
            {onSaveAsOrgDefault && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onSaveAsOrgDefault}
                className="text-muted-foreground"
              >
                <T context="button to save columns as org default in column config dialog">
                  Save as Org Default
                </T>
              </Button>
            )}
          </div>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            <T context="done button for column config dialog">Done</T>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
